import React, { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, FormControl, Radio, RadioGroup, FormControlLabel, Grid, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getInvoiceById } from '../../../service/Invoice/invoice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

export default function UpdateReturnStatusById({ invoiceId, onConfirmStatusChange, handleStatus, selectReturnState, fetchData }: {
  invoiceId: any;
  onConfirmStatusChange: (newStatus: string) => void;
  handleStatus: (any) => void;
  selectReturnState: any;
  fetchData: () => void;
}) {
  const [rows, setRows] = useState([]);
  const [openImage, setOpenImage] = useState(false);
  const [selectedImageList, setSelectedImageList] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!invoiceId) return;

      try {
        const response = await getInvoiceById(invoiceId);
        const items = response?.data?.data?.items || [];

        const mappedRows = items.map((item: any) => ({
          id: item.id,
          product_name: item.product_name,
          qty: item.quantity,
          original_price: parseFloat(item.original_price),
          return_reason: item.return_reason?.reason || 'N/A',
          return_image: item.return_image || [],
        }));

        setRows(mappedRows);
      } catch (error) {
        setRows([]);
      }
    };

    fetchInvoiceData();
  }, [invoiceId]);

  const dialogColumns: GridColDef[] = [
    { field: 'product_name', headerName: 'Product Name', width: 200, flex: 1 },
    { field: 'qty', headerName: 'Quantity', width: 150, flex: 1 },
    { field: 'original_price', headerName: 'Price', width: 150 },
    { field: 'return_reason', headerName: 'Return Reason', width: 200 },
    {
      field: 'return_image',
      headerName: 'Return Image',
      minWidth: 170,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const imageSrcList = params.row?.return_image?.split(',') || [];

        return (
          imageSrcList.length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setSelectedImageList(imageSrcList); 
                setSelectedImageIndex(0);           
                setOpenImage(true);
              }}
            >
              <img
                src={imageSrcList[0]}
                alt="Return"
                style={{
                  height: 40,
                  width: 40,
                  borderRadius: '4px',
                  objectFit: 'cover',
                  padding: '2px',
                  border: '1px solid #ccc',
                }}
              />
              {imageSrcList.length > 1 && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    fontSize: '0.75rem',
                    color: '#999',
                  }}
                >
                  +{imageSrcList.length - 1}
                </Box>
              )}
            </Box>
          )
        );
      }
    }
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ height: 250, borderRadius: 2 }}>
            <DataGrid
              rows={rows}
              columns={dialogColumns}
              hideFooter
              disableColumnMenu
            />
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
        >
          <FormControl component="fieldset">
            <RadioGroup row value={selectReturnState} onChange={handleStatus} sx={{ gap: 3 }}>
              <FormControlLabel value="approved" control={<Radio />} label="Approve" />
              <FormControlLabel value="liquidated" control={<Radio />} label="Liquidated" />
              <FormControlLabel value="rejected" control={<Radio />} label="Rejected" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Dialog open={openImage} onClose={() => setOpenImage(false)}
        PaperProps={{
          sx: {
            position: 'relative',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 2, textAlign: 'center' }}>
          <IconButton
            onClick={() => setOpenImage(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>

          {selectedImageList.length > 1 && (
            <>
              <IconButton
                onClick={() => setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : selectedImageList.length - 1))}
                sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)' }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              <IconButton
                onClick={() => setSelectedImageIndex((prev) => (prev < selectedImageList.length - 1 ? prev + 1 : 0))}
                sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)' }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}

          <img
            src={selectedImageList[selectedImageIndex]}
            alt="Return Preview"
            style={{
              maxHeight: '500px',
              maxWidth: '100%',
              borderRadius: '8px',
            }}
          />
        </DialogContent>
      </Dialog>
    </Box >
  );
}